import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  AlertTriangle, Package, Bell, Check, X, ChevronRight,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stock Alert Badge
const StockStatusBadge = ({ status }) => {
  const config = {
    out_of_stock: { color: 'bg-red-100 text-red-800', label: 'Out of Stock', icon: X },
    low_stock: { color: 'bg-yellow-100 text-yellow-800', label: 'Low Stock', icon: AlertTriangle },
    warning: { color: 'bg-orange-100 text-orange-800', label: 'Getting Low', icon: AlertTriangle }
  };
  
  const { color, label, icon: Icon } = config[status] || config.low_stock;
  
  return (
    <Badge className={`${color} border-0 gap-1`}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

// Stock Alert Widget for Admin Dashboard
export const StockAlertWidget = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stock-alerts/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch stock alerts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="animate-pulse flex items-center gap-3">
            <div className="w-10 h-10 bg-muted rounded-lg"></div>
            <div className="flex-1">
              <div className="h-4 bg-muted rounded w-24 mb-2"></div>
              <div className="h-3 bg-muted rounded w-32"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!summary || (summary.out_of_stock_count === 0 && summary.low_stock_count === 0)) {
    return (
      <Card className="border-green-200 bg-green-50/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
              <Check className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium text-green-800">Stock Levels OK</p>
              <p className="text-sm text-green-600">All products have sufficient stock</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={summary.out_of_stock_count > 0 ? 'border-red-200 bg-red-50/50' : 'border-yellow-200 bg-yellow-50/50'}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              summary.out_of_stock_count > 0 
                ? 'bg-red-100 text-red-600' 
                : 'bg-yellow-100 text-yellow-600'
            }`}>
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium">
                {summary.out_of_stock_count > 0 
                  ? `${summary.out_of_stock_count} Out of Stock` 
                  : `${summary.low_stock_count} Low Stock`}
              </p>
              <p className="text-sm text-muted-foreground">
                {summary.total_alerts} items need attention
              </p>
            </div>
          </div>
          <Link to="/admin/stock-alerts">
            <Button variant="ghost" size="sm" className="gap-1">
              View <ChevronRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>

        {/* Quick list of affected items */}
        {summary.items?.slice(0, 3).map((item, idx) => (
          <div key={idx} className="mt-3 pt-3 border-t flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-muted-foreground" />
              <span className="truncate max-w-[150px]">{item.product_name}</span>
              {item.variant_size && (
                <span className="text-muted-foreground">
                  {item.variant_size}/{item.variant_color}
                </span>
              )}
            </div>
            <StockStatusBadge status={item.status} />
          </div>
        ))}
        
        {summary.items?.length > 3 && (
          <p className="mt-2 text-xs text-muted-foreground text-center">
            +{summary.items.length - 3} more items
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// Full Stock Alerts Page
const StockAlertsPage = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stock-alerts/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch stock alerts:', error);
      toast.error('Failed to load stock alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="icon">
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Archivo, sans-serif' }}>
                  Stock Alerts
                </h1>
                <p className="text-sm text-muted-foreground">
                  Monitor inventory levels across all products
                </p>
              </div>
            </div>
            <Button onClick={fetchSummary} variant="outline" className="gap-2">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className={summary.out_of_stock_count > 0 ? 'border-red-300' : ''}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-red-600">{summary.out_of_stock_count || 0}</p>
                <p className="text-sm text-muted-foreground">Out of Stock</p>
              </CardContent>
            </Card>
            <Card className={summary.low_stock_count > 0 ? 'border-yellow-300' : ''}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-yellow-600">{summary.low_stock_count || 0}</p>
                <p className="text-sm text-muted-foreground">Low Stock</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">{summary.total_alerts || 0}</p>
                <p className="text-sm text-muted-foreground">Total Alerts</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{summary.threshold || 5}</p>
                <p className="text-sm text-muted-foreground">Alert Threshold</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Items List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Inventory Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-muted-foreground">Loading...</div>
            ) : summary?.items?.length === 0 ? (
              <div className="py-8 text-center">
                <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="font-medium text-green-700">All Stock Levels OK</p>
                <p className="text-sm text-muted-foreground">
                  No products are below the {summary?.threshold || 5} unit threshold
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {summary?.items?.map((item, idx) => (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-lg border flex items-center justify-between ${
                      item.status === 'out_of_stock' 
                        ? 'bg-red-50 border-red-200' 
                        : 'bg-yellow-50 border-yellow-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Package className={`w-5 h-5 ${
                        item.status === 'out_of_stock' ? 'text-red-500' : 'text-yellow-500'
                      }`} />
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        {item.variant_size && (
                          <p className="text-sm text-muted-foreground">
                            Size: {item.variant_size} · Color: {item.variant_color}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className={`text-lg font-bold ${
                          item.current_stock <= 0 ? 'text-red-600' : 'text-yellow-600'
                        }`}>
                          {item.current_stock}
                        </p>
                        <p className="text-xs text-muted-foreground">units</p>
                      </div>
                      <StockStatusBadge status={item.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default StockAlertsPage;
